import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Check, ChevronDown, Search } from "lucide-react";

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  /** Small label chip shown inside the trigger before the summary. */
  leadingLabel?: string;
  options: MultiSelectOption[];
  /** Currently selected values. */
  selected: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Show a search box above the list (default true). */
  searchable?: boolean;
  /** When provided, an "Apply" button is shown in the footer that calls this
   *  and closes the popover (use to defer side effects like an API call). */
  onApply?: () => void;
  /** Label for the apply button (default "Apply"). */
  applyLabel?: string;
  /** Called (in addition to clearing) when the "Clear all" button is clicked. */
  onClearAll?: () => void;
  /** When set, the trigger summary reads "<n> <selectedNoun> Selected"
   *  (e.g. "2 Organization Selected") instead of the default "<n> selected". */
  selectedNoun?: string;
}

/**
 * Multi-select dropdown (checkbox list) matching the app's pill Dropdown style.
 * Built on Radix Popover for portal positioning + focus handling.
 */
export function MultiSelectDropdown({
  leadingLabel,
  options,
  selected,
  onChange,
  placeholder = "Select…",
  disabled = false,
  className = "",
  searchable = true,
  onApply,
  applyLabel = "Apply",
  onClearAll,
  selectedNoun,
}: MultiSelectDropdownProps) {
  const [query, setQuery] = React.useState("");

  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const summary =
    selected.length === 0
      ? placeholder
      : selectedNoun
        ? `${selected.length} ${selectedNoun} Selected`
        : selected.length === 1
          ? options.find((o) => o.value === selected[0])?.label ?? "1 selected"
          : `${selected.length} selected`;

  return (
    <PopoverPrimitive.Root onOpenChange={(o) => !o && setQuery("")}>
      <PopoverPrimitive.Trigger
        disabled={disabled}
        className={`group inline-flex h-[38px] min-w-[190px] items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 text-sm text-foreground outline-none transition-colors hover:border-indigo-400/60 focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500/20 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2">
          {leadingLabel && (
            <span className="shrink-0 text-[11px] font-semibold tracking-wider text-muted-foreground">
              {leadingLabel}
            </span>
          )}
          <span className={`truncate ${selected.length ? "text-foreground" : "text-muted-foreground"}`}>
            {summary}
          </span>
        </span>
        <ChevronDown className="size-4 shrink-0 transition-transform group-data-[state=open]:rotate-180" />
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          className="z-[200] w-[var(--radix-popover-trigger-width)] min-w-[220px] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        >
          {searchable && (
            <div className="flex items-center gap-2 border-b border-border px-2.5 py-2">
              <Search className="size-3.5 shrink-0 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
          )}

          {selected.length > 0 && (
            <div className="flex items-center justify-between border-b border-border px-2.5 py-1.5">
              <span className="text-[11px] font-medium text-muted-foreground">{selected.length} selected</span>
              <button
                type="button"
                onClick={() => {
                  onChange([]);
                  onClearAll?.();
                }}
                className="text-[11px] font-semibold text-indigo-600 hover:underline dark:text-indigo-400"
              >
                Clear all
              </button>
            </div>
          )}

          <div className="max-h-[240px] overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <p className="px-2.5 py-3 text-center text-xs text-muted-foreground">No options</p>
            ) : (
              filtered.map((opt) => {
                const checked = selected.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggle(opt.value)}
                    className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-sm outline-none transition-colors hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
                  >
                    <span
                      className={`flex size-4 shrink-0 items-center justify-center rounded border transition-colors ${
                        checked
                          ? "border-indigo-500 bg-indigo-500 text-white"
                          : "border-border"
                      }`}
                    >
                      {checked && <Check className="size-3" />}
                    </span>
                    <span className="truncate">{opt.label}</span>
                  </button>
                );
              })
            )}
          </div>

          {onApply && (
            <div className="border-t border-border p-2">
              <PopoverPrimitive.Close asChild>
                <button
                  type="button"
                  onClick={onApply}
                  className="w-full rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-indigo-700"
                >
                  {applyLabel}
                </button>
              </PopoverPrimitive.Close>
            </div>
          )}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
